import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MatCheckbox } from '@angular/material/checkbox'
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatTab, MatTabGroup } from '@angular/material/tabs'
import { MatToolbar } from '@angular/material/toolbar'
import {
  ElderBasicPaneLayoutComponent,
  ElderDialogService,
  ElderHeaderComponent,
  ElderLocalizedInputComponent,
  ElderPaneActionsComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneSubtitleComponent,
  ElderPaneTitleComponent,
  ElderSelectComponent,
  ElderTabGroupRoutingDirective,
  ElderThemeDirective,
  ElderToastService,
  ElderValidationErrorDirective,
  I18nText,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { IdValidator } from '../../../../../features/validators/id-validator'
import { ColumnsConf } from '../../../../orders/work-items/columns/columns-conf'
import { ColumnsConfDetailComponent } from '../../../../orders/work-items/columns/columns-conf-detail/columns-conf-detail.component'
import { ProductionListPropertyDefinitionOrganizerComponent } from '../../../../orders/work-items/properties/production-list-property-definition-organizer/production-list-property-definition-organizer.component'
import { ItemSortSpec } from '../../../../shopfloor/workcenters/types/items/item-sort-spec'
import { ItemSortSpecDetailComponent } from '../../../../shopfloor/workcenters/types/items/item-sort-spec-detail/item-sort-spec-detail.component'
import { ProductionListScope } from '../prod-list-scope'
import { ProdListScopeSelectDirective } from '../prod-list-scope-select.directive'
import { ProductionListConf } from '../production-list-conf'
import { ProductionListConfService } from '../production-list-conf.service'

@Component({
  selector: 'app-production-list-conf-detail',
  templateUrl: './production-list-conf-detail.component.html',
  styleUrls: ['./production-list-conf-detail.component.scss'],
  imports: [
    ElderPaneComponent,
    ElderPaneHeaderComponent,
    ElderPaneActionsComponent,
    ElderPaneContentComponent,
    ElderPaneTitleComponent,
    ElderBasicPaneLayoutComponent,
    ElderPaneSubtitleComponent,
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    MatTabGroup,
    ElderTabGroupRoutingDirective,
    MatTab,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    ElderValidationErrorDirective,
    ElderSelectComponent,
    ProdListScopeSelectDirective,
    ElderLocalizedInputComponent,
    MatCheckbox,
    ProductionListPropertyDefinitionOrganizerComponent,
    ColumnsConfDetailComponent,
    ItemSortSpecDetailComponent,
    ElderHeaderComponent,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class ProductionListConfDetailComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private _conf$ = new BehaviorSubject<ProductionListConf>(null)

  private _creationMode: boolean

  @Input()
  public embedded = false

  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()

  public readonly idValidatorPattern = IdValidator.pattern
  private prodListConfService = inject(ProductionListConfService)
  private dialogService = inject(ElderDialogService)
  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    if (!this.prodListConf) {
      this._creationMode = true
      this.prodListConf = this.initNew()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set prodListConf(conf: ProductionListConf) {
    if (conf) {
      this._conf$.next(conf)
    }
  }

  public get prodListConf(): ProductionListConf {
    return this._conf$.getValue()
  }

  public get prodListConf$(): Observable<ProductionListConf> {
    return this._conf$.asObservable()
  }

  public get creationMode(): boolean {
    return this._creationMode
  }

  public get deletionEnabled(): boolean {
    return !this.creationMode
  }

  /***************************************************************************
   *                                                                         *
   * Component Events                                                        *
   *                                                                         *
   **************************************************************************/

  public onSave(event: Event): void {
    this.save()
  }

  public onDelete(event: Event): void {
    this.delete()
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private save(): void {
    if (this.creationMode && this.prodListConf.id) {
      this.prodListConfService.findById(this.prodListConf.id).subscribe({
        next: (_) => this.toastService.info('ID already exists!'),
        error: (_) => this.saveToDB(), // Id does not exist yet -> save
      })
    } else {
      this.saveToDB()
    }
  }

  private saveToDB(): void {
    this.prodListConfService.save(this.prodListConf).subscribe({
      next: (updated) => {
        this.prodListConf = updated
        this.toastService.info('messages.persisting_successful')
      },
      error: (err) => this.toastService.error('messages.saving_failed', err),
    })
  }

  private delete(): void {
    this.dialogService
      .showConfirm({
        title: 'messages.deleting_confirmation_title',
        message: 'messages.deleting_confirmation_msg',
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.prodListConfService.delete(this.prodListConf).subscribe({
            next: (_) => this.close.emit(),
            error: (err) => this.toastService.error('messages.deleting_failed', err),
          })
        }
      })
  }

  private emitClose(event?: Event): void {
    this.close.emit(event)
  }

  private initNew(): ProductionListConf {
    return new ProductionListConf(
      'Neu',
      ProductionListScope.MULTI,
      [new I18nText('de', 'Neu'), new I18nText('en', 'New')], // TODO hardcoded for now
      '',
      [],
      new ColumnsConf([]),
      new ItemSortSpec(),
      false,
      true
    )
  }
}
